import type { MovePayload, IPlayerState } from "@shared/types";
import type * as THREE from "three";
import {
  getLocalPlayer,
  getLocalPlayerId,
  getGameState,
  getPlayerMeshes,
  keys,
} from "./gameState";
import { sendMessage } from "./socket";
import { calculateNextPosition } from "@shared/movement";
import { updateMouthDirection } from "./utils";
import { updatePlayerMouthDirection as updatePlayerMouthDirectionFromModel } from "./models/playerModel";

// Keep a buffer of recent move predictions for reconciliation
interface MovePrediction {
  x: number;
  y: number;
  z: number;
  timestamp: number;
  inputSequence?: number;
}

const movePredictions: MovePrediction[] = [];
const MAX_PREDICTION_BUFFER = 20;
let inputSequence = 0;

// Add debug logging utilities

// Track when the last log was sent to prevent console spam
let lastDebugLogTime = 0;
const DEBUG_LOG_INTERVAL = 2000; // Log at most once every 2 seconds

// Debug log for significant events that won't spam the console
function debugLog(message: string, force = false): void {
  const now = Date.now();

  // Only log if forced or if enough time has passed since last log
  if (force || now - lastDebugLogTime > DEBUG_LOG_INTERVAL) {
    console.log(`[PACMAN-DEBUG ${new Date().toISOString()}] ${message}`);
    lastDebugLogTime = now;
  }
}

// Track the largest correction we've seen to help identify serious issues
let largestCorrection = 0;

/**
 * Client-side movement prediction with reconciliation.
 * Uses identical physics as the server to reduce corrections.
 */
export function processMovement(deltaTime = 1): void {
  const localPlayer = getLocalPlayer();
  const localPlayerId = getLocalPlayerId();
  const gameState = getGameState();
  const playerMeshes = getPlayerMeshes();

  if (!localPlayer || !gameState || !localPlayerId) return;

  // Determine input direction based on keys
  let inputDirection: "up" | "down" | "left" | "right" | null = null;

  if (keys.w || keys.ArrowUp) {
    inputDirection = "up";
  } else if (keys.s || keys.ArrowDown) {
    inputDirection = "down";
  } else if (keys.a || keys.ArrowLeft) {
    inputDirection = "left";
  } else if (keys.d || keys.ArrowRight) {
    inputDirection = "right";
  }

  // No movement if no direction
  if (inputDirection === null) return;

  // Use shared movement logic to calculate next position
  const movement = calculateNextPosition(
    gameState,
    localPlayer,
    inputDirection,
    deltaTime
  );

  // Debug logging - remove in production
  // console.log("Movement calculation:", {
  //   deltaTime,
  //   direction: inputDirection,
  //   valid: movement.valid,
  //   position: { x: movement.x, y: movement.y },
  // });

  // If the move is not valid, don't apply it
  if (!movement.valid) return;

  // Record the prediction for later reconciliation
  const timestamp = Date.now();
  const nextInputSequence = inputSequence++;

  const prediction = {
    timestamp,
    x: movement.x,
    y: movement.y,
    z: localPlayer.z,
    inputSequence: nextInputSequence,
  };

  movePredictions.push(prediction);

  // Keep prediction buffer at reasonable size
  if (movePredictions.length > MAX_PREDICTION_BUFFER) {
    movePredictions.shift();
  }

  // Apply the move locally (optimistic update)
  localPlayer.x = movement.x;
  localPlayer.y = movement.y;

  // Update mesh position
  if (playerMeshes[localPlayerId]) {
    playerMeshes[localPlayerId].position.set(movement.x, 0.4, movement.y);

    // Update the mouth orientation based on movement direction
    updatePlayerMouthDirection(playerMeshes[localPlayerId], movement.direction);
  }

  // Send to server with input sequence for reconciliation
  const movePayload: MovePayload = {
    playerId: localPlayerId,
    x: movement.x,
    y: movement.y,
    z: localPlayer.z,
    inputSequence: nextInputSequence,
  };
  sendMove(movePayload);
}

/**
 * Updates the player's mouth direction based on movement
 */
function updatePlayerMouthDirection(
  playerMesh: THREE.Group,
  direction: number
): void {
  updatePlayerMouthDirectionFromModel(playerMesh, direction);
}

export function sendMove(movePayload: MovePayload): void {
  sendMessage("move", movePayload);
}

/**
 * Updates player position and mesh based on server movement data
 */
export function updatePlayerPosition(
  playerId: string,
  x: number,
  y: number,
  z: number
): void {
  const gameState = getGameState();
  const playerMeshes = getPlayerMeshes();
  const localPlayerId = getLocalPlayerId();

  if (!gameState || !gameState.players[playerId]) return;

  // Update game state
  gameState.players[playerId].x = x;
  gameState.players[playerId].y = y;
  gameState.players[playerId].z = z;

  // Update mesh position
  if (playerMeshes[playerId]) {
    // For other players, use simple interpolation
    if (playerId !== localPlayerId) {
      playerMeshes[playerId].position.set(x, 0.4, y);
    }

    // Determine movement direction from position change
    const previousX = playerMeshes[playerId].userData.previousX;
    const previousY = playerMeshes[playerId].userData.previousY;

    if (previousX !== undefined && previousY !== undefined) {
      const dx = x - previousX;
      const dy = y - previousY;

      // Update mouth direction based on movement
      updateMouthDirection(playerMeshes[playerId], dx, dy);
    }

    // Save current position for next comparison
    playerMeshes[playerId].userData.previousX = x;
    playerMeshes[playerId].userData.previousY = y;
  }
}

/**
 * Handles server correction for local player position
 * Uses simplified reconciliation approach
 */
export function handleServerCorrection(
  localId: string,
  localPlayer: IPlayerState,
  x: number,
  y: number,
  z: number,
  inputSequence?: number
): void {
  const playerMeshes = getPlayerMeshes();

  // Calculate the correction distance
  const correctionDistance = Math.hypot(localPlayer.x - x, localPlayer.y - y);

  // Track largest correction for debugging
  if (correctionDistance > largestCorrection) {
    largestCorrection = correctionDistance;
    // Force logging for new max corrections
    debugLog(
      `New max correction: ${correctionDistance.toFixed(3)} units`,
      true
    );
  }

  // Log any significantly large corrections (potential teleports)
  if (correctionDistance > 0.5) {
    debugLog(
      `Large position correction: ${correctionDistance.toFixed(3)} units ` +
        `from (${localPlayer.x.toFixed(2)},${localPlayer.y.toFixed(2)}) ` +
        `to (${x.toFixed(2)},${y.toFixed(2)})`
    );
  }

  // If we have an input sequence, try to find that specific prediction
  if (inputSequence !== undefined) {
    // Find the prediction with matching sequence number
    const predictionIndex = movePredictions.findIndex(
      (p) => p.inputSequence === inputSequence
    );

    if (predictionIndex >= 0) {
      // Check if correction is minimal (server agrees with our prediction)
      const prediction = movePredictions[predictionIndex];
      const errorDistance = Math.hypot(prediction.x - x, prediction.y - y);

      if (errorDistance < 0.2) {
        // Server confirmed our prediction - we can keep our local state
        return;
      }

      // Server correction needed - clear predictions after this point
      movePredictions.splice(predictionIndex);

      if (errorDistance > 0.3) {
        debugLog(
          `Prediction error: ${errorDistance.toFixed(
            3
          )} units for input ${inputSequence}`
        );
      }
    }
  }

  // Apply server correction directly
  localPlayer.x = x;
  localPlayer.y = y;
  localPlayer.z = z;

  // Apply to mesh
  if (playerMeshes[localId]) {
    playerMeshes[localId].position.set(x, 0.4, y);
  }
}
